import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Icon,
  IconButton,
  Hidden,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';

import Helmet from 'react-helmet';
import { convertObjToArr, sortArrByKey } from '../../utils';
import ContextBar from '../../components/ContextBar';
import Search from '../../components/Search';
import MsgBox from '../../components/MsgBox';
import Course from './Course';
import useStyles from './styles';

import Actions from '../../store/ducks/course';

const ListCourses = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const theme = useTheme();
  const screenXs = useMediaQuery(theme.breakpoints.only('xs'));
  const {
    product: { productName, trainingsLabel },
    course: {
      list: coursesObj,
      allCoursesLoaded,
      error: { list: error },
      loading: { list: requestingCourses },
    },
  } = useSelector(state => state);
  const [viewMode, setViewMode] = useState('grid');
  const [filter, setFilter] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [loading, setLoading] = useState(true);
  const [coursesArr, setCoursesArr] = useState([]);

  const requestCoursesList = useCallback(() => {
    dispatch(Actions.requestCoursesList());
  }, [dispatch]);

  useEffect(() => {
    setLoading(requestingCourses);
  }, [requestingCourses]);

  useEffect(() => {
    if (allCoursesLoaded) return;
    requestCoursesList();
  }, [allCoursesLoaded, requestCoursesList]);

  useEffect(() => {
    setCoursesArr(sortArrByKey(convertObjToArr(coursesObj), 'name'));
  }, [coursesObj]);

  useEffect(() => {
    setViewMode(screenXs ? 'list' : 'grid');
  }, [screenXs]);

  const coursesFiltered = useMemo(
    () =>
      coursesArr.filter(course => {
        const normalizeStr = str =>
          str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        const normalizedFilter = normalizeStr(filter);

        return Object.keys(course).some(key => {
          if (typeof course[key] === 'string') {
            return new RegExp(normalizedFilter, 'i').test(
              normalizeStr(course[key]),
            );
          }
          return false;
        });
      }),
    [coursesArr, filter],
  );

  const statusList = useMemo(() => {
    if (loading) {
      return 'LOADING';
    }
    if (error) {
      return 'ERROR';
    }
    if (!loading && !error && !coursesArr.length) {
      return 'EMPTY';
    }
    return 'READY';
  }, [error, loading, coursesArr]);

  const handleViewMode = () =>
    setViewMode(viewMode === 'grid' ? 'list' : 'grid');
  const handleFilterCourses = query => setFilter(query);
  const handleToggleSearch = (value = !showSearch) => setShowSearch(value);

  return (
    <>
      <Helmet>
        <title>{`${trainingsLabel} - ${productName}`}</title>
      </Helmet>
      <ContextBar title={showSearch && screenXs ? '' : trainingsLabel}>
        <Box className={classes.contextBarActions}>
          <Hidden xsUp={showSearch && screenXs}>
            <Tooltip
              title={
                viewMode === 'grid'
                  ? 'Visualização em lista'
                  : 'Visualização em grade'
              }
            >
              <IconButton className="view-mode-toggle" onClick={handleViewMode}>
                <Icon>{viewMode === 'grid' ? 'view_list' : 'view_module'}</Icon>
              </IconButton>
            </Tooltip>
          </Hidden>
          <Search
            toggleHandler={handleToggleSearch}
            filterHandler={handleFilterCourses}
          />
        </Box>
      </ContextBar>
      <Box className={classes.container}>
        {statusList === 'EMPTY' && (
          <MsgBox
            type="INFO"
            icon="search_off"
            msg="Você ainda não possuí treinamentos disponíveis"
          />
        )}
        {statusList === 'ERROR' && (
          <MsgBox
            type="WARNING"
            msg="Não foi possível carregar a listagem de treinamentos"
            buttonText="Tentar novamente"
            buttonAction={requestCoursesList}
          />
        )}
        {statusList !== 'ERROR' && statusList !== 'EMPTY' && (
          <Box className={`content ${viewMode}`}>
            {statusList === 'LOADING' && <Course loading view={viewMode} />}
            {statusList === 'READY' &&
              coursesFiltered.map(item => (
                <Course key={item.id} id={item.id} view={viewMode} />
              ))}
          </Box>
        )}
      </Box>
    </>
  );
};

export default ListCourses;
